import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Card, CardBody, Col, Container, Row } from "reactstrap";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import CommonTable from "components/Common/TableContainer";
import Loaders from "components/Common/Loaders";
import { pascalToCamel, urlEndPoint } from "helpers/GeneralHelper";
import {
  fetchAccountExecutiveDashboard,
  fetchAccountExecutiveDashboardMeta,
} from "model/DashboardModel";

import "./account-executive-dashboard.scss";

const ALL_CANDIDATES_PREFILL_STORAGE_KEY = "allCandidatesPrefillFilters";

const REVIEW_STATUS_META = {
  1: { label: "Red", tone: "red" },
  2: { label: "Yellow", tone: "yellow" },
  3: { label: "Green", tone: "green" },
};

const STATUS_ACCESSORS = new Set([
  "statuslastweek",
  "statusthisweek",
  "lastreviewstatusid",
  "reviewstatusid",
  "lastreviewstatushtml",
  "reviewstatushtml",
]);

function parseReviewStatusLabelFromHtml(reviewStatusHtml = "") {
  const raw = String(reviewStatusHtml ?? "").trim();
  if (!raw) return "";
  return raw.replace(/<[^>]*>/g, "").trim();
}

const CLIENT_ACCESSORS = new Set([
  "client",
  "clientname",
  "companyname",
]);

const DEFAULT_COLUMNS = [
  { Header: "SN", accessor: "sn" },
  { Header: "Client", accessor: "client" },
  { Header: "Status Last Week", accessor: "statusLastWeek" },
  { Header: "Status This Week", accessor: "statusThisWeek" },
  { Header: "Requirements", accessor: "requirements" },
  { Header: "Hires This Month", accessor: "hiresThisMonth" },
  { Header: "Hires Last Month", accessor: "hiresLastMonth" },
];

function formatReviewStatusCell(value) {
  const id = String(value ?? "").trim();
  if (!id) {
    return <span className="text-muted">—</span>;
  }
  const meta = REVIEW_STATUS_META[id];
  if (!meta) {
    return <span className="text-muted">—</span>;
  }
  return (
    <span className={`ae-rag-pill ae-rag-pill--${meta.tone}`}>{meta.label}</span>
  );
}

function resolveInitialTableSection(summary, reviewedCount, pendingCount) {
  if (Number(summary?.reviewed) > 0 || reviewedCount > 0) return "reviewed";
  if (Number(summary?.pending) > 0 || pendingCount > 0) return "pending";
  return "all";
}

function buildColumnsFromMeta(metaPayload) {
  const meta = metaPayload?.data ?? metaPayload ?? {};
  const rawCols =
    meta?.reviewedColumns ??
    meta?.table?.columns ??
    meta?.reviewed?.columns ??
    meta?.reviewedTable?.columns ??
    meta?.weeklyReviewColumns;

  if (!Array.isArray(rawCols) || !rawCols.length) {
    return DEFAULT_COLUMNS;
  }

  const mapped = rawCols
    .map((col) => {
      const title = col?.title ?? col?.Title ?? col?.Header ?? "?";
      const field = col?.field ?? col?.Field ?? col?.accessor ?? col?.Accessor;
      const accessor =
        typeof field === "string" ? pascalToCamel(field) : "";
      return {
        Header: title,
        accessor,
        type: col?.type ?? col?.Type,
      };
    })
    .filter((c) => c.accessor);

  const hasSn = mapped.some((c) => String(c.accessor).toLowerCase() === "sn");
  return hasSn ? mapped : [{ Header: "SN", accessor: "sn" }, ...mapped];
}

function coerceRowData(raw, idx, columnAccessors) {
  const o = raw && typeof raw === "object" ? { ...raw } : {};
  const camel = {};
  Object.keys(o).forEach((k) => {
    camel[pascalToCamel(k)] = o[k];
  });
  const merged = { ...o, ...camel };

  const pickStr = (...keys) => {
    for (const k of keys) {
      if (!k || typeof k !== "string") continue;
      const val = merged[k];
      if (val !== undefined && val !== null && val !== "") return val;
    }
    return "";
  };

  merged.client = pickStr(
    "client",
    "clientName",
    "companyName",
    "clientDisplayName"
  );

  merged.statusLastWeek = pickStr(
    "statusLastWeek",
    "lastWeekStatus",
    "ragLastWeek",
    "StatusLastWeek"
  );
  merged.statusThisWeek = pickStr(
    "statusThisWeek",
    "thisWeekStatus",
    "ragThisWeek",
    "status",
    "StatusThisWeek"
  );

  merged.requirements = pickStr("requirements", "requirement", "Requirements");

  merged.hiresThisMonth =
    merged.hiresThisMonth ??
    merged.HiresThisMonth ??
    merged.hireCount ??
    merged.HireCount ??
    merged.hires_this_month ??
    0;
  merged.hiresLastMonth =
    merged.hiresLastMonth ??
    merged.HiresLastMonth ??
    merged.lastHireCount ??
    merged.LastHireCount ??
    merged.hires_last_month ??
    0;

  merged.reviewStatusHtml = pickStr("reviewStatusHtml", "ReviewStatusHtml");
  merged.lastReviewStatusHtml = pickStr("lastReviewStatusHtml", "LastReviewStatusHtml");

  merged.statusLastWeekId =
    merged.lastReviewStatusId ??
    merged.LastReviewStatusId ??
    merged.statusLastWeekId ??
    "";
  merged.statusThisWeekId =
    merged.reviewStatusId ??
    merged.ReviewStatusId ??
    merged.statusThisWeekId ??
    "";

  const statusThisWeekLabel =
    REVIEW_STATUS_META[String(merged.statusThisWeekId).trim()]?.label ||
    parseReviewStatusLabelFromHtml(merged.reviewStatusHtml) ||
    (merged.statusThisWeekId ? String(merged.statusThisWeekId) : "");
  const statusLastWeekLabel =
    REVIEW_STATUS_META[String(merged.statusLastWeekId).trim()]?.label ||
    parseReviewStatusLabelFromHtml(merged.lastReviewStatusHtml) ||
    (merged.statusLastWeekId ? String(merged.statusLastWeekId) : "");

  merged.statusLastWeek = merged.statusLastWeek || statusLastWeekLabel;
  merged.statusThisWeek = merged.statusThisWeek || statusThisWeekLabel;

  merged.isReviewed = Boolean(merged.isReviewed ?? merged.IsReviewed);

  merged.sn = idx + 1;

  columnAccessors.forEach((acc) => {
    const cur = merged[acc];
    const empty = cur === undefined || cur === null || cur === "";
    if (!empty) return;
    const head = acc.charAt(0).toUpperCase();
    const pascalAcc = `${head}${acc.slice(1)}`;
    if (merged[pascalAcc] !== undefined && merged[pascalAcc] !== null) {
      merged[acc] = merged[pascalAcc];
    }
  });

  const resolvedClientId =
    merged.clientId ??
    merged.ClientId ??
    merged.client_id ??
    merged.companyId ??
    merged.CompanyId ??
    merged.company_id ??
    merged.id ??
    merged.Id;
  merged.clientId =
    resolvedClientId !== undefined && resolvedClientId !== null && resolvedClientId !== ""
      ? String(resolvedClientId).trim()
      : "";

  return merged;
}

function applyAeDashboardColumnCells(baseColumns, onClientClick) {
  return (baseColumns || []).map((col) => {
    const accessorKey = String(col?.accessor ?? "")
      .trim()
      .toLowerCase();

    if (STATUS_ACCESSORS.has(accessorKey)) {
      const isThisWeekColumn =
        accessorKey === "statusthisweek" ||
        accessorKey === "reviewstatusid" ||
        accessorKey === "reviewstatushtml";
      return {
        ...col,
        Cell: ({ row }) => {
          const original = row?.original ?? {};
          const statusHtml = String(
            isThisWeekColumn
              ? original.reviewStatusHtml ?? ""
              : original.lastReviewStatusHtml ?? ""
          ).trim();
          if (statusHtml) {
            return (
              <span
                className="account-executive-weekly-review-status-html"
                dangerouslySetInnerHTML={{ __html: statusHtml }}
              />
            );
          }
          const statusId = isThisWeekColumn
            ? original.statusThisWeekId ?? original.reviewStatusId
            : original.statusLastWeekId ?? original.lastReviewStatusId;
          return formatReviewStatusCell(statusId);
        },
      };
    }

    if (CLIENT_ACCESSORS.has(accessorKey)) {
      return {
        ...col,
        Cell: ({ row }) => {
          const original = row?.original ?? {};
          const label =
            original.client ||
            original.clientName ||
            original.companyName ||
            "—";
          const clientId =
            original.clientId ??
            original.ClientId ??
            original.client_id ??
            "";
          if (!clientId) {
            return <span>{label}</span>;
          }
          return (
            <button
              type="button"
              className="btn btn-link p-0 text-start ae-client-link"
              onClick={() => onClientClick(original)}
            >
              {label}
            </button>
          );
        },
      };
    }

    return col;
  });
}

const AccountExecutiveWeeklyDashboard = () => {
  const navigate = useNavigate();
  const tableSectionRef = useRef(null);
  const showPipelineAction = true;

  const [loading, setLoading] = useState(true);
  const [activeTableSection, setActiveTableSection] = useState("reviewed");
  const [weekDaysText, setWeekDaysText] = useState("");
  const [summary, setSummary] = useState({});
  const [reviewedRows, setReviewedRows] = useState([]);
  const [pendingRows, setPendingRows] = useState([]);
  const [columns, setColumns] = useState(DEFAULT_COLUMNS);

  const noopModal = useCallback(() => {}, []);

  const allRows = useMemo(() => {
    const seen = new Set();
    const merged = [];
    [...reviewedRows, ...pendingRows].forEach((row) => {
      const id = String(row?.clientId ?? "").trim();
      const key = id || `row-${merged.length}`;
      if (seen.has(key)) return;
      seen.add(key);
      merged.push(row);
    });
    return merged.map((row, index) => ({ ...row, sn: index + 1 }));
  }, [reviewedRows, pendingRows]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [dashRes, metaRes] = await Promise.all([
        fetchAccountExecutiveDashboard(),
        fetchAccountExecutiveDashboardMeta(),
      ]);

      const metaCols = buildColumnsFromMeta(metaRes ?? {});
      setColumns(metaCols);
      const colAccessors = metaCols.map((c) => c.accessor);

      if (dashRes?.success === false) {
        toast.error(dashRes?.message || "Unable to load dashboard");
        setWeekDaysText("");
        setSummary({});
        setReviewedRows([]);
        setPendingRows([]);
        return;
      }

      const d = dashRes?.data ?? dashRes ?? {};
      setWeekDaysText(d.weekDaysText ?? "");

      const s = d.summary ?? {};
      setSummary({
        totalClients: Number(s.totalClients) || 0,
        reviewed: Number(s.reviewed) || 0,
        pending: Number(s.pending) || 0,
        red: Number(s.red) || 0,
        yellow: Number(s.yellow) || 0,
        green: Number(s.green) || 0,
        redPercent: Number(s.redPercent) || 0,
        yellowPercent: Number(s.yellowPercent) || 0,
        greenPercent: Number(s.greenPercent) || 0,
      });

      const rev = Array.isArray(d.reviewed) ? d.reviewed : [];
      const pen = Array.isArray(d.pending) ? d.pending : [];

      const reviewedMapped = rev.map((r, i) => coerceRowData(r, i, colAccessors));
      const pendingMapped = pen.map((r, i) => coerceRowData(r, i, colAccessors));

      setReviewedRows(reviewedMapped);
      setPendingRows(pendingMapped);
      setActiveTableSection(
        resolveInitialTableSection(s, reviewedMapped.length, pendingMapped.length)
      );
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const scrollToTableArea = useCallback(() => {
    requestAnimationFrame(() =>
      tableSectionRef.current?.scrollIntoView?.({ behavior: "smooth", block: "start" })
    );
  }, []);

  const showReviewedTableSection = useCallback(() => {
    setActiveTableSection("reviewed");
    scrollToTableArea();
  }, [scrollToTableArea]);

  const showPendingTableSection = useCallback(() => {
    setActiveTableSection("pending");
    scrollToTableArea();
  }, [scrollToTableArea]);

  const showAllClientsTableSection = useCallback(() => {
    setActiveTableSection("all");
    scrollToTableArea();
  }, [scrollToTableArea]);

  /** Same navigation as `client-review.jsx` → client-review-edit with client id. */
  const handleEdit = useCallback(
    (row = {}) => {
      const clientId = row?.clientId ?? row?.ClientId ?? row?.client_id ?? row?.id ?? row?.Id;
      if (!clientId) return;
      navigate(`/${urlEndPoint.clientReviewEdit}`, {
        state: { clientId: String(clientId).trim() },
      });
    },
    [navigate]
  );

  const handlePipeline = useCallback(
    (row = {}) => {
      const clientId = String(
        row?.clientId ?? row?.ClientId ?? row?.client_id ?? ""
      ).trim();
      const clientName = String(
        row?.clientName ??
          row?.client ??
          row?.companyName ??
          row?.clientDisplayName ??
          ""
      ).trim();
      if (!clientId) {
        toast.error("Client id is missing");
        return;
      }
      const prefillFilters = {
        clientId,
        clientIdLabel: clientName,
        clientName,
      };
      try {
        sessionStorage.setItem(
          ALL_CANDIDATES_PREFILL_STORAGE_KEY,
          JSON.stringify(prefillFilters)
        );
      } catch {
        // navigation state remains the fallback
      }
      navigate(`/${urlEndPoint.allCandidates}`, {
        state: { prefillFilters },
      });
    },
    [navigate]
  );

  const tableColumns = useMemo(
    () => applyAeDashboardColumnCells(columns, handleEdit),
    [columns, handleEdit]
  );

  const activeTableRows = useMemo(() => {
    if (activeTableSection === "pending") return pendingRows;
    if (activeTableSection === "reviewed") return reviewedRows;
    return allRows;
  }, [activeTableSection, pendingRows, reviewedRows, allRows]);

  const activeTableTitle =
    activeTableSection === "pending"
      ? "Pending"
      : activeTableSection === "reviewed"
        ? "Reviewed"
        : "All Clients";

  const activeTablePageName =
    activeTableSection === "pending"
      ? "Weekly Review Pending"
      : activeTableSection === "reviewed"
        ? "Weekly Review Reviewed"
        : "Weekly Review Clients";

  if (loading) {
    return (
      <div className="page-content ae-dashboard-root">
        <Container fluid className="ae-dashboard-container px-3 px-md-4 pt-3">
          <Card className="ae-table-card ae-dashboard-loader-card">
            <CardBody className="ae-dashboard-loader-body">
              <div className="ae-dashboard-loader-center">
                <Loaders />
              </div>
            </CardBody>
          </Card>
        </Container>
      </div>
    );
  }

  const showReviewedTable = columns?.length > 0;

  return (
    <div className="page-content ae-dashboard-root">
      <Container fluid className="ae-dashboard-container px-3 px-md-4">
        <Row className="align-items-center mt-6">
          <Col className="mb-2">
          <h2 className="ae-week-banner-heading mt-3">
              <span className="ae-week-banner-title">Weekly Review</span>
              <span className="ae-week-banner-chip" title="Selected report week">
                <i className="bx bx-calendar-week" aria-hidden />
                <span className="ae-week-banner-chip-inner">
                  <span className="ae-week-banner-chip-prefix">Week of</span>
                  <span className="ae-week-banner-dates">
                    {weekDaysText || "—"}
                  </span>
                </span>
              </span>
            </h2>
          </Col>
          <Col xs="auto" className="mt-2">
            <Button color="outline-primary" size="sm" type="button" onClick={load}>
              <i className="mdi mdi-refresh me-1" aria-hidden /> Refresh
            </Button>
          </Col>
        </Row>
       <hr />

        <Row className="ae-metrics-row row-cols-1 row-cols-md-2 row-cols-xl-4 g-3 gx-lg-4 mt-1 align-items-stretch">
          <Col className="ae-metric-col">
            <Card
              className={`ae-card-metric ae-card-metric--bar ae-card-metric--bar-rose${
                activeTableSection === "all" ? " ae-card-metric--active" : ""
              }`}
              role="button"
              tabIndex={0}
              onClick={showAllClientsTableSection}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  showAllClientsTableSection();
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <CardBody className="ae-card-metric-body">
                <span className="ae-metric-label">Clients</span>
                <span className="ae-icon-badge" aria-hidden>
                  <i className="bx bx-crown" />
                </span>
                <div className="ae-metric-main">
                  <span className="ae-metric-value">{summary.totalClients}</span>
                </div>
                <div className="ae-metric-flex-fill" aria-hidden />
                <div className="ae-metric-bottom">
                  <p className="ae-metric-caption">Analytics for last week</p>
                  <div className="ae-metric-footer ae-metric-footer--link">
                    <button
                      type="button"
                      className="btn btn-link btn-sm fw-semibold p-0 shadow-none ae-metric-link"
                      onClick={showAllClientsTableSection}
                    >
                      View
                    </button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col className="ae-metric-col">
            <Card
              className={`ae-card-metric ae-card-metric--bar ae-card-metric--bar-forest${
                activeTableSection === "reviewed" ? " ae-card-metric--active" : ""
              }`}
              role="button"
              tabIndex={0}
              onClick={showReviewedTableSection}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  showReviewedTableSection();
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <CardBody className="ae-card-metric-body">
                <span className="ae-metric-label">Reviewed</span>
                <span className="ae-icon-badge" aria-hidden>
                  <i className="bx bx-list-ul" />
                </span>
                <div className="ae-metric-main">
                  <span className="ae-metric-value">{summary.reviewed}</span>
                </div>
                <div className="ae-metric-flex-fill" aria-hidden />
                <div className="ae-metric-bottom">
                  <p className="ae-metric-caption">Analytics for last week</p>
                  <div className="ae-metric-footer ae-metric-footer--link">
                    <button
                      type="button"
                      className="btn btn-link btn-sm fw-semibold p-0 shadow-none ae-metric-link"
                      onClick={showReviewedTableSection}
                    >
                      View
                    </button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col className="ae-metric-col">
            <Card
              className={`ae-card-metric ae-card-pending-hot ae-card-metric--bar ae-card-metric--bar-pending${
                activeTableSection === "pending" ? " ae-card-metric--active" : ""
              }`}
              role="button"
              tabIndex={0}
              onClick={showPendingTableSection}
              onKeyDown={(e) => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  showPendingTableSection();
                }
              }}
              style={{ cursor: "pointer" }}
            >
              <CardBody className="ae-card-metric-body">
                <span className="ae-metric-label">Pending</span>
                <span className="ae-icon-badge" aria-hidden>
                  <i className="bx bx-error-alt" />
                </span>
                <div className="ae-metric-main">
                  <span className="ae-metric-value">{summary.pending}</span>
                </div>
                <div className="ae-metric-flex-fill" aria-hidden />
                <div className="ae-metric-bottom">
                  <p className="ae-metric-caption ae-metric-caption--on-dark">
                    Analytics for last week
                  </p>
                  <div className="ae-metric-footer ae-metric-footer--link">
                    <button
                      type="button"
                      className="btn btn-link btn-sm text-white p-0 shadow-none fw-bold"
                      onClick={showPendingTableSection}
                    >
                      Re-View
                    </button>
                  </div>
                </div>
              </CardBody>
            </Card>
          </Col>
          <Col className="ae-metric-col">
            <Card className="ae-card-metric ae-card-metric--bar ae-card-metric--bar-gold ae-dist-card">
              <CardBody className="ae-card-metric-body">
                <div className="ae-dist-stack">
                  <div className="ae-dist-row ae-dist-red">
                    <span>Red</span>
                    <small className="text-muted">{summary.red}</small>
                    <span className="ae-dist-pill">%{summary.redPercent}</span>
                  </div>
                  <div className="ae-dist-row ae-dist-yellow">
                    <span>Yellow</span>
                    <small className="text-muted">{summary.yellow}</small>
                    <span className="ae-dist-pill">%{summary.yellowPercent}</span>
                  </div>
                  <div className="ae-dist-row ae-dist-green">
                    <span>Green</span>
                    <small className="text-muted">{summary.green}</small>
                    <span className="ae-dist-pill">%{summary.greenPercent}</span>
                  </div>
                </div>
                <div className="ae-metric-flex-fill" aria-hidden />
                <div className="ae-metric-bottom">
                  <p className="ae-metric-caption mb-0">Analytics for last week</p>
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>

        <div ref={tableSectionRef} className="ae-dashboard-table-section">
          <Card className="ae-table-card mt-4">
            <CardBody>
              <h5 className="fw-bold mb-3 ae-table-title">
                {activeTableTitle}
                <span className="text-muted fw-normal ms-2">
                  ({activeTableRows.length})
                </span>
              </h5>
              {showReviewedTable ? (
                activeTableRows.length > 0 ? (
                  <CommonTable
                    columns={tableColumns}
                    data={activeTableRows}
                    isActionRequired
                    onEdit={handleEdit}
                    onPipeline={showPipelineAction ? handlePipeline : undefined}
                    isEditAccess
                    isCheckboxLeftSide={false}
                    isPagination
                    pageName={activeTablePageName}
                    setCommonFilterModalOpen={noopModal}
                    showCopyCsvPrintToolbar
                  />
                ) : (
                  <div className="text-muted py-4">
                    No {activeTableTitle.toLowerCase()} clients for this week.
                  </div>
                )
              ) : (
                <div className="text-muted py-4">
                  Unable to load table columns.
                </div>
              )}
            </CardBody>
          </Card>
        </div>

      </Container>
    </div>
  );
};

export default AccountExecutiveWeeklyDashboard;
